import React from 'react';

/**
 *
 * STANDARD COMPONENTS
 *
 */
import App, { AppProps } from './components/app/App';
import ErrorBoundary, { ErrorBoundaryProps } from './components/errorboundary/ErrorBoundary';
import ErrorBoundaryContent, { ErrorBoundaryContentProps } from './components/errorboundary/ErrorBoundaryContent';
import Header, { HeaderProps } from './components/header/Header';
import Loading, { LoadingProps } from './components/loading/Loading';
import Footer, { FooterProps } from './components/footer/Footer';

/**
 *
 * ASYNC / REACT LAZY COMPONENTS
 *
 */
type ComponentClassAsync<T> = Promise<{ default: React.ComponentClass<T> }>;

export class ComponentFactory {
  static get<T>(component: any): new () => React.Component<T> {
    return component;
  }

  static getAsync<T>(component: any): ComponentClassAsync<T> {
    return component;
  }

  // Core Components
  static get App() {
    return ComponentFactory.get<AppProps>(App);
  }

  static get AuthAsync() {
    return import(/* webpackChunkName: "Auth" */ './pages/auth/Auth');
  }

  static get ErrorBoundary() {
    return ComponentFactory.get<ErrorBoundaryProps>(ErrorBoundary);
  }

  static get ErrorBoundaryContent() {
    return ComponentFactory.get<ErrorBoundaryContentProps>(ErrorBoundaryContent);
  }

  static get ErrorPageAsync() {
    return import(/* webpackChunkName: "ErrorPage" */ './pages/errorpage/ErrorPage');
  }

  static get Footer() {
    return ComponentFactory.get<FooterProps>(Footer);
  }

  static get Header() {
    return ComponentFactory.get<HeaderProps>(Header);
  }

  static get Loading() {
    return ComponentFactory.get<LoadingProps>(Loading);
  }

  // Rental-related Components
  static get RentalListAsync() {
    return import(/* webpackChunkName: "RentalList" */ './components/rentallist/RentalList');
  }

  static get RentalMapAsync() {
    return import(/* webpackChunkName: "RentalMap" */ './components/rentalmap/RentalMap');
  }

  static get RentalSearchBarAsync() {
    return import(/* webpackChunkName: "RentalSearchBar" */ './components/rentalsearchbar/RentalSearchBar');
  }

  static get RentalsAsync() {
    return import(/* webpackChunkName: "Rentals" */ './pages/rentals/Rentals');
  }

  static get RentalPageAsync() {
    return import(/* webpackChunkName: "RentalPage" */ './pages/rentalpage/RentalPage');
  }

  static get RentalCardAsync() {
    return import(/* webpackChunkName: "RentalCard" */ './components/rentalcard/RentalCard');
  }

  static get CardAsync() {
    return import(/* webpackChunkName: "Card" */ './components/card/Card');
  }

  static get CheckboxAsync() {
    return import(/* webpackChunkName: "Checkbox" */ './components/checkbox/Checkbox');
  }

  static get RentalImageGalleryAsync() {
    return import(/* webpackChunkName: "RentalImageGallery" */ './components/rentalimagegallery/RentalImageGallery');
  }

  static get RentalDetailsCardAsync() {
    return import(/* webpackChunkName: "RentalDetailsCard" */ './components/rentaldetailscard/RentalDetailsCard');
  }

  static get CancelShowingAsync() {
    return import(/* webpackChunkName: "CancelShowing" */ './pages/cancelshowing/CancelShowing');
  }

  static get ProspectPageAsync() {
    return import(/* webpackChunkName: "ProspectPage" */ './pages/prospectpage/ProspectPage');
  }
}
